import React, {useEffect, useState} from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App.tsx";
import reportWebVitals from "./reportWebVitals";

import {InteractionStatus, InteractionRequiredAuthError} from "@azure/msal-browser";
import {MsalProvider, useMsal} from "@azure/msal-react";
import {getMsalInstance, loginRequest} from "./AppSetting";

// Initialize MSAL PublicClientApplication
const msalInstance = getMsalInstance();

/**
 * getAuthData: returns full AuthenticationResult, including token and ID claims
 */
async function getAuthData(instance, inProgress) {
    // Don't attempt to get auth data if an interaction is in progress
    if (inProgress !== InteractionStatus.None) {
        return null;
    }

    const accounts = instance.getAllAccounts();
    if (accounts.length === 0) {
        await instance.loginRedirect(loginRequest);
        return null; // redirecting
    }
    try {
        const result = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
        });
        return result;
    } catch (e) {
        if (e instanceof InteractionRequiredAuthError) {
            // Token expired or consent required - redirect to login
            await instance.loginRedirect(loginRequest);
            return null;
        }
        // Log other errors for debugging
        console.error("Authentication error:", e);
        throw e; // Re-throw unexpected errors
    }
}

/**
 * AuthWrapper handles retrieval of auth data and renders App once ready
 */
const AuthWrapper = () => {
    const {instance, inProgress} = useMsal();
    const [acquiredToken, setAcquiredToken] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        setError(null);
        let isMounted = true;
        getAuthData(instance, inProgress)
            .then((data) => {
                if (isMounted && data) setAcquiredToken(data);
            })
            .catch((e) => {
                if (isMounted) setError(e.message || "Authentication failed");
            });
        return () => {
            isMounted = false;
        };
    }, [instance, inProgress]);

    if (error) {
        return <div className="text-center mt-5 text-danger">Error: {error}</div>;
    }
    if (!acquiredToken) {
        return <div className="text-center mt-5">Loading authentication...</div>;
    }
    return <App acquiredToken={acquiredToken}/>;
};

async function initializeAndRender() {
    try {
        await msalInstance.initialize();
        // Handle redirect response after returning from identity provider
        await msalInstance.handleRedirectPromise();
    } catch (e) {
        console.error("MSAL initialization error", e);
        return;
    }

    const root = ReactDOM.createRoot(document.getElementById("root"));
    root.render(
        <MsalProvider instance={msalInstance}>
            <AuthWrapper/>
        </MsalProvider>
    );
}

initializeAndRender();

reportWebVitals();
